import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "../../../../components/Select";
import { TextInput } from "../../../../components/TextInput";
import CDTAndCustomProcedureSelect, {
  CDTAndCustomProcedureValue,
} from "../../../../components/CDTAndCustomProcedureSelect";
import { TrashIcon, ArrowsMoveIcon } from "../../../../components/Icons";
import { colors } from "../../../../theme/colors";
import { SelectOption } from "../../../../components/Select/Select";
import {
  StepCard,
  StepHeader,
  StepTitleContainer,
  StepTitle,
  StepActions,
  ActionButton,
  DragHandle,
  StepContent,
  LeftColumn,
  RightColumn,
  FieldRow,
  FieldHalf,
} from "./EditableTreatmentStep.styled";

export interface EditableStepData {
  id?: number;
  stepNumber: number;
  stepName: string;
  cdtCodes: string[];
  customProcedureId?: number;
  resolvedCdtCodes: string[];
  explanation: string;
  durationMinutes: string;
  fee: string;
  timeUntilNextStepValue: string;
  timeUntilNextStepUnit: "days" | "weeks" | "months";
}

interface EditableTreatmentStepProps {
  step: EditableStepData;
  isLastStep: boolean;
  onChange: (field: keyof EditableStepData, value: unknown) => void;
  onDelete: () => void;
}

const TIME_VALUE_OPTIONS: SelectOption[] = Array.from(
  { length: 12 },
  (_, i) => ({
    value: i + 1,
    label: String(i + 1),
  }),
);

const EditableTreatmentStep: React.FC<EditableTreatmentStepProps> = ({
  step,
  isLastStep,
  onChange,
  onDelete,
}) => {
  const { t } = useTranslation();

  const cdtAndCustomProcedureValue: CDTAndCustomProcedureValue = useMemo(
    () => ({
      cdtCodes: step.cdtCodes,
      customProcedureId: step.customProcedureId,
    }),
    [step.cdtCodes, step.customProcedureId],
  );

  const handleCdtAndCustomProcedureChange = (
    value: CDTAndCustomProcedureValue,
    allCdtCodes: string[],
    allDescriptions: string[],
  ) => {
    onChange("cdtCodes", value.cdtCodes);
    onChange("customProcedureId", value.customProcedureId);
    onChange("resolvedCdtCodes", allCdtCodes);
    onChange("explanation", allDescriptions.filter(Boolean).join("\n"));
  };

  const TIME_UNIT_OPTIONS: SelectOption[] = useMemo(
    () => [
      { value: "days", label: t("editableStep.timeUnits.days") },
      { value: "weeks", label: t("editableStep.timeUnits.weeks") },
      { value: "months", label: t("editableStep.timeUnits.months") },
    ],
    [t],
  );

  const handleFeeBlur = () => {
    if (!step.fee || step.fee.trim() === "") {
      onChange("fee", "0");
    }
  };

  const handleDurationChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    onChange("durationMinutes", value);
  };

  const handleFeeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // Allow digits and one decimal point
    let value = e.target.value
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*)\./g, "$1");
    // Remove leading zeros (but keep "0" for decimals like "0.5")
    if (value.length > 1 && value.startsWith("0") && value[1] !== ".") {
      value = value.replace(/^0+/, "") || "0";
    }
    onChange("fee", value);
  };

  return (
    <StepCard>
      <StepHeader>
        <StepTitleContainer>
          <StepTitle>
            {t("editableStep.stepTitle", {
              number: step.stepNumber,
              name: step.stepName,
            })}
          </StepTitle>
        </StepTitleContainer>
        <StepActions>
          <ActionButton onClick={onDelete}>
            <TrashIcon width="20px" height="20px" fill={colors.status.error} />
          </ActionButton>
          <DragHandle>
            <ArrowsMoveIcon
              width="20px"
              height="20px"
              fill={colors.text.secondary}
            />
          </DragHandle>
        </StepActions>
      </StepHeader>

      <StepContent>
        <LeftColumn>
          <CDTAndCustomProcedureSelect
            label={t("editableStep.cdtCode")}
            placeholder={t("editableStep.cdtCodePlaceholder")}
            value={cdtAndCustomProcedureValue}
            onChange={handleCdtAndCustomProcedureChange}
            fullWidth
          />

          <FieldRow>
            <FieldHalf>
              <TextInput
                label={t("editableStep.duration")}
                placeholder=""
                value={step.durationMinutes}
                onChange={handleDurationChange}
                type="text"
              />
            </FieldHalf>
            <FieldHalf>
              <TextInput
                label={t("editableStep.fee")}
                placeholder="0"
                value={step.fee}
                onChange={handleFeeChange}
                onBlur={handleFeeBlur}
                type="text"
                required
              />
            </FieldHalf>
          </FieldRow>

          <FieldRow>
            <FieldHalf>
              <Select
                label={t("editableStep.timeUntilNext")}
                value={
                  step.timeUntilNextStepValue
                    ? Number(step.timeUntilNextStepValue)
                    : ""
                }
                onChange={(value) =>
                  onChange("timeUntilNextStepValue", String(value))
                }
                options={TIME_VALUE_OPTIONS}
                disabled={isLastStep}
                fullWidth
              />
            </FieldHalf>
            <FieldHalf>
              <Select
                label={t("editableStep.units")}
                value={step.timeUntilNextStepUnit}
                onChange={(value) => onChange("timeUntilNextStepUnit", value)}
                options={TIME_UNIT_OPTIONS}
                disabled={isLastStep}
                fullWidth
              />
            </FieldHalf>
          </FieldRow>
        </LeftColumn>

        <RightColumn>
          <TextInput
            label={t("editableStep.explanation")}
            placeholder={t("editableStep.explanationPlaceholder")}
            value={step.explanation}
            onChange={(e) => onChange("explanation", e.target.value)}
            multiline
            height={205}
            rows={8}
          />
        </RightColumn>
      </StepContent>
    </StepCard>
  );
};

export default EditableTreatmentStep;
